import { Link } from "react-router-dom";
import { Reveal, Eyebrow, BracketLink, BracketButton, Marker } from "@/components/atoms";
import { LogoMark } from "@/components/Logo";
import { IMAGES } from "@/data/site";

const CATS = ["Cartridge", "Band", "Flexible", "Strip", "Air", "Coil", "Box", "Thermocouples"];

export default function MockupHome() {
  return (
    <div className="bg-bone min-h-screen text-navy">
      <div className="bg-navy no-print sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center justify-between">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-paper/70" data-testid="mockup-ribbon">REDESIGN MOCKUP 1 / 2 — BACKER MARATHON HOMEPAGE</p>
          <Link to="/" data-testid="mockup-home-back" className="font-mono text-[10px] tracking-[0.2em] uppercase text-paper/70 hover:text-ember transition-colors">&larr; PROPOSAL</Link>
        </div>
      </div>

      {/* site nav */}
      <nav className="bg-white border-b border-linec">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={36} />
            <span className="font-body font-bold tracking-tight text-navy">BACKER MARATHON</span>
          </div>
          <div className="hidden md:flex gap-8 font-body text-sm text-navy/70">
            {["Products", "Capabilities", "Resources", "Company"].map((l) => <span key={l}>{l}</span>)}
          </div>
          <BracketButton primary testid="mockup-nav-quote">Request a Quote</BracketButton>
        </div>
      </nav>

      {/* hero */}
      <header className="bg-white border-b border-linec">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal><Eyebrow>ELECTRIC HEATING ELEMENTS &amp; SENSORS</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-body font-medium tracking-[-0.035em] leading-[1.02] text-5xl lg:text-7xl mt-8">
                Industrial heat,<br />engineered <span className="text-ember">to spec.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-mutedx text-lg mt-8 max-w-lg leading-relaxed">
                Cartridge, band, flexible and strip heaters built for OEM lines and direct-fit replacement —
                custom designs, no minimum order.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="flex flex-wrap gap-4 mt-10">
              <BracketLink primary testid="mockup-hero-cta-1">Find a heater</BracketLink>
              <BracketLink testid="mockup-hero-cta-2">Browse the catalog</BracketLink>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="relative h-[380px] lg:h-[480px] overflow-hidden">
              <img src={IMAGES.foundry} alt="Molten thermal engineering" className="w-full h-full object-cover" />
              <Marker className="top-3 left-3" />
              <p className="absolute bottom-5 left-5 font-mono text-[10px] tracking-[0.25em] uppercase text-paper/80">FIG. 01 — PROCESS FLOOR</p>
            </div>
          </Reveal>
        </div>
      </header>

      {/* stats */}
      <div className="relative bg-mist border-b border-linec">
        <Marker className="-bottom-1 left-1/4 hidden md:block" />
        <Marker className="-bottom-1 left-2/4 hidden md:block" />
        <Marker className="-bottom-1 left-3/4 hidden md:block" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-linec">
          {[["EST. 1996", "Heating since day one"], ["1600°F", "HotRod cartridge ceiling"], ["300 W/IN²", "Peak watt density"], ["NIBE GROUP", "Backer family member"]].map(([v, l]) => (
            <div key={l} className="py-8 px-4 text-center" data-testid={`mockup-stat-${v.replace(/[^A-Z0-9]+/gi, "-")}`}>
              <p className="font-body font-semibold text-xl lg:text-3xl text-navy">{v}</p>
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-dim mt-2">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* orange band */}
      <section className="bg-ember text-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <h2 className="font-body font-medium tracking-[-0.03em] leading-[1.02] text-4xl lg:text-6xl">Heaters for every process.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-navy/80 text-lg leading-relaxed">
              Over 17,000 cartridge heaters, band heaters, silicone rubber heaters and temperature sensors in stock —
              ISO 14001 certified, backed by live engineering support on the phone.
            </p>
          </Reveal>
        </div>
      </section>

      {/* categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <Reveal><Eyebrow>THE CATALOG</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-body font-medium tracking-[-0.03em] text-3xl lg:text-5xl mt-6">Search it. Filter it. Quote it.</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
          {CATS.map((c, i) => (
            <Reveal key={c} delay={i * 0.04}>
              <div className="relative border border-linec bg-white p-6 hover:border-ember transition-colors cursor-pointer h-full" data-testid={`mockup-category-${c.toLowerCase()}`}>
                <Marker className="-top-1 -right-1" />
                <p className="font-mono text-[10px] text-ember">{String(i + 1).padStart(2, "0")}</p>
                <p className="font-body font-semibold text-lg mt-4 text-navy">{c} {c === "Thermocouples" ? "" : "Heaters"}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* spotlight */}
      <section className="border-t border-linec bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative overflow-hidden">
              <img src={IMAGES.heroCoil} alt="HotRod cartridge heater" className="w-full h-[360px] object-cover" />
              <Marker className="bottom-3 right-3" />
            </div>
          </Reveal>
          <div>
            <Reveal><Eyebrow>SPOTLIGHT — HOTROD™ SERIES</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-body font-medium tracking-[-0.03em] text-3xl lg:text-5xl mt-6">Swaged cartridge, up to 1600°F.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-mutedx mt-6 leading-relaxed">Precision-ground sheaths at 300 watts per square inch for the fastest ramp-up in the line — with square and non-swaged variants for every tooling setup.</p>
            </Reveal>
            <Reveal delay={0.15} className="flex flex-wrap gap-2 mt-8">
              {["1600°F", "300 W/IN²", "120–480 V", "NO MOQ"].map((s) => (
                <span key={s} className="font-mono text-[10px] tracking-[0.15em] border border-linec rounded-sm px-3 py-1.5 text-navy/80">{s}</span>
              ))}
            </Reveal>
            <Reveal delay={0.2} className="mt-10">
              <BracketLink to="/demo" primary testid="mockup-home-cta-portal">Quote this series &rarr;</BracketLink>
            </Reveal>
          </div>
        </div>
      </section>

      <footer className="bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row justify-between gap-4">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-paper/50">BACKER MARATHON · PART OF THE NIBE / BACKER GROUP</p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-paper/50">MOCKUP — NOT FINAL COPY</p>
        </div>
      </footer>
    </div>
  );
}
